import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { v4 } from "uuid";
import { config } from "../config";

interface UnAuthTokenResponse {
  accessToken: string;
}

interface DecodedToken {
  exp: number;
  [key: string]: unknown;
}

// Track if token is being loaded to prevent duplicate calls
let tokenLoadingPromise: Promise<UnAuthTokenResponse> | null = null;

export const getUnAuthToken = async (
  baseUrl: string,
  xApiKeyParam?: string,
): Promise<UnAuthTokenResponse> => {
  const correlationId = v4();
  
  // Get x-api-key from parameter, window runtime config, or config module
  let xApiKey: string | undefined = xApiKeyParam;
  
  if (!xApiKey && typeof window !== "undefined") {
    const runtimeConfig = (window as Window & { __authShellConfig?: { xApiKey?: string } }).__authShellConfig;
    xApiKey = runtimeConfig?.xApiKey;
  }
  
  // Also try to get from config module as fallback
  if (!xApiKey) {
    try {
      const { config: configModule } = await import("../config");
      xApiKey = configModule.env("xApiKey") ?? undefined;
    } catch (error) {
      console.warn("[UnAuthToken] Failed to import config module:", error);
    }
  }
  
  if (!xApiKey) {
    const errorMsg = "[UnAuthToken] x-api-key is missing. Cannot fetch unauthenticated token. " +
      "Please ensure xApiKey is configured in your environment config.";
    console.error(errorMsg);
    throw new Error("x-api-key is required but not found in configuration");
  }
  
  const unAuthHeaders: Record<string, string> = {
    "x-correlation-id": correlationId.toString(),
    client_scope_identifier: "Web",
    "x-api-key": xApiKey,
  };
  
  const requestUrl = `${baseUrl}/unauth/customers/token`;
  
  // Log request details for debugging
  console.log("[UnAuthToken] Fetching token:", {
    url: requestUrl,
    baseUrl,
    hasXApiKey: !!xApiKey,
    xApiKeyPrefix: xApiKey?.substring(0, 8) + "...",
    correlationId: correlationId.toString(),
    headers: Object.keys(unAuthHeaders),
  });
  
  return axios({
    url: requestUrl,
    method: "GET",
    headers: unAuthHeaders,
    params: { isHeadersPassed: true },
    withCredentials: false, // Don't send cookies with token requests
    timeout: 30000, // 30 second timeout
  })
    .then((res) => {
      console.log("[UnAuthToken] ✅ Token fetched successfully", {
        hasAccessToken: !!res.data?.accessToken,
      });
      return res.data;
    })
    .catch((error) => {
      // Enhanced error logging with more details
      const errorDetails = {
        url: requestUrl,
        method: "GET",
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        headers: Object.keys(unAuthHeaders),
        isAxiosError: axios.isAxiosError(error),
        isCorsError: error.message?.includes("Network Error") || error.message?.includes("CORS"),
      };
      
      console.error("[UnAuthToken] ❌ Failed to fetch token:", errorDetails);
      
      // Provide more specific error messages
      if (error.response?.status === 401) {
        throw new Error("Unauthorized: Invalid or missing x-api-key. Please check your API configuration.");
      } else if (error.response?.status === 403) {
        throw new Error("Forbidden: API key does not have permission to access this endpoint.");
      } else if (errorDetails.isCorsError) {
        throw new Error("CORS error: Unable to connect to API. Check Vite proxy configuration or API CORS settings.");
      } else if (error.response?.data?.message) {
        throw new Error(`API Error: ${error.response.data.message}`);
      }
      
      throw error;
    });
};

export const setUnAuthTokenAndTimer = async (
  baseUrl: string,
  xApiKey?: string,
) => {
  // Check if token already exists and is not expired
  const existingToken = config.env("unAuthToken");
  const tokenExpiry = config.env("timer");

  if (existingToken && tokenExpiry) {
    const currentTime = Math.floor(Date.now() / 1000);
    const expiryTime = parseInt(tokenExpiry, 10);
    // If token exists and has a valid expiry in the future, don't reload
    if (Number.isFinite(expiryTime) && expiryTime > currentTime) {
      console.log("[UnAuthToken] Using existing token, not reloading");
      return;
    }
  }

  // If already loading, return the existing promise
  if (tokenLoadingPromise) {
    console.log("[UnAuthToken] Token already loading, reusing promise");
    return tokenLoadingPromise;
  }

  // Get xApiKey if not provided as parameter
  let apiKey = xApiKey;
  if (!apiKey && typeof window !== "undefined") {
    const runtimeConfig = (window as Window & { __authShellConfig?: { xApiKey?: string } }).__authShellConfig;
    apiKey = runtimeConfig?.xApiKey;
  }

  // Start loading token
  tokenLoadingPromise = getUnAuthToken(baseUrl, apiKey)
    .then((res) => {
      const token = jwtDecode<DecodedToken>(res.accessToken);
      config.setEnv("timer", token.exp);
      config.setEnv("unAuthToken", res.accessToken);
      tokenLoadingPromise = null; // Clear promise after completion
      return res;
    })
    .catch((err) => {
      tokenLoadingPromise = null; // Clear promise on error
      throw err;
    });

  return tokenLoadingPromise;
};
